import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Joi from 'joi';
import AvailableOn from '../../AvailableOn/AvailableOn';
import './CorporateGifting.css';

const initialForm = {
    name: '',
    company_name: '',
    contact: '',
    email: '',
    location: '',
    occasion: '',
    message: '',
};

const corporateSchema = Joi.object({
    name: Joi.string().min(2).max(50).required().messages({
        'string.empty': 'Name is required',
        'string.min': 'Name must be at least 2 characters long',
    }),
    company_name: Joi.string().min(2).max(100).required().messages({
        'string.empty': 'Company Name is required',
    }),
    contact: Joi.string().pattern(/^[0-9]{10,15}$/).required().messages({
        'string.pattern.base': 'Contact must be a valid phone number (10-15 digits)',
        'string.empty': 'Contact number is required',
    }),
    email: Joi.string().email({ tlds: { allow: false } }).required().messages({
        'string.email': 'Please enter a valid email address',
        'string.empty': 'Email is required',
    }),
    location: Joi.string().required().messages({
        'string.empty': 'Location is required',
    }),
    occasion: Joi.string().required().messages({
        'string.empty': 'Please select an occasion',
    }),
    message: Joi.string().allow(''),
});

const OCCASION_OPTIONS = [
    'Employee Gifting',
    'Pantry Snacking',
    'Festive Gifting',
    'Wholesale / Retail',
    'Personal Gifting',
    'Corporate Gifting',
];

function CorporateGifting() {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { error } = corporateSchema.validate(form, { abortEarly: false });
        if (error) {
            error.details.forEach((err) => toast.error(err.message));
            return;
        }

        setLoading(true);
        try {
            // Replace with your actual service call, e.g.:
            // await sendCorporateInquiry(form);
            await new Promise((res) => setTimeout(res, 1000)); // mock delay
            toast.success('Inquiry sent successfully! We will contact you soon.');
            setForm(initialForm);
        } catch (err) {
            console.error('Inquiry failed:', err);
            toast.error('Failed to send inquiry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Kuku Namkeen | Corporate Gifting</title>
                <link rel="canonical" href="https://kukunamkeen.com/corporate-gifting" />
            </Helmet>

            {/* Page Header Banner */}
            <section className="corp-header-title">
                <div className="corp-container">
                    <h1>CORPORATE GIFTING</h1>
                </div>
            </section>
            <img
                src="/kuku-assets/images/border-bottom-title.png"
                className="corp-header-border header-title-image"
                alt=""
            />

            {/* Main Section */}
            <section className="corporate-page section-space">
                <div className="corp-container">
                    <div className="corp-form-card">

                        {/* Left: illustration */}
                        <div className="corp-illustration">
                            <p>Elevate Business Relationships with Exclusive and Customized Corporate Gifting Solutions</p>
                            <img
                                src="/kuku-assets/images/inquiry.png"
                                alt="Corporate Gifting"
                            />
                        </div>

                        {/* Right: form */}
                        <div className="corp-form-side">
                            <h3 className="corp-section-title">Inquiry for Corporate</h3>

                            <form onSubmit={handleSubmit}>
                                <div className="corp-grid">

                                    {/* Name */}
                                    <div className="corp-field">
                                        <input
                                            type="text"
                                            name="name"
                                            id="corp_name"
                                            placeholder=" "
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="corp_name">Name</label>
                                    </div>

                                    {/* Company Name */}
                                    <div className="corp-field">
                                        <input
                                            type="text"
                                            name="company_name"
                                            id="corp_company_name"
                                            placeholder=" "
                                            value={form.company_name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="corp_company_name">Company Name</label>
                                    </div>

                                    {/* Mobile */}
                                    <div className="corp-field">
                                        <input
                                            type="tel"
                                            name="contact"
                                            id="corp_contact"
                                            placeholder=" "
                                            value={form.contact}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="corp_contact">Your Mobile</label>
                                    </div>

                                    {/* Email */}
                                    <div className="corp-field">
                                        <input
                                            type="email"
                                            name="email"
                                            id="corp_email"
                                            placeholder=" "
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="corp_email">mail@yourmail.com</label>
                                    </div>

                                    {/* Location */}
                                    <div className="corp-field">
                                        <input
                                            type="text"
                                            name="location"
                                            id="corp_location"
                                            placeholder=" "
                                            value={form.location}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label htmlFor="corp_location">Location</label>
                                    </div>

                                    {/* Occasion – Select */}
                                    <div className="corp-field">
                                        <select
                                            name="occasion"
                                            id="corp_occasion"
                                            value={form.occasion}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Open this select menu</option>
                                            {OCCASION_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                        <label htmlFor="corp_occasion">Works with selects</label>
                                    </div>

                                </div>

                                {/* Submit */}
                                <div className="corp-submit-wrap">
                                    <button
                                        type="submit"
                                        className="corp-submit-btn"
                                        disabled={loading}
                                    >
                                        {loading ? 'Sending...' : 'Send Inquiry'}
                                    </button>
                                </div>
                            </form>
                        </div>

                    </div>
                </div>
            </section>

            <AvailableOn />
            <ToastContainer position="top-right" autoClose={5000} />
        </>
    );
}

export default CorporateGifting;
